import {
  PICKUP_OPTIMYZER_FILTERS,
  PICKUP_OPTIMYZER_FILTER_NAME,
} from "./constants";
import {
  isNotAssignedPickup,
  isNotCompletedPickup,
} from "../../../modules/Dispatch/RouteExecution/filterUtils";
import appConstants from "../../../../appConstants";
import { uniq } from "lodash";

const getDefaultFilters = () => {
  return [
    {
      name: PICKUP_OPTIMYZER_FILTER_NAME.isAssigned,
      label: PICKUP_OPTIMYZER_FILTERS.assigned,
      filterFunc: ({pickup, terminalOffset}) => isNotCompletedPickup({pickup, terminalOffset}),
    },
    {
      name: PICKUP_OPTIMYZER_FILTER_NAME.isUnassigned,
      label: PICKUP_OPTIMYZER_FILTERS.unassigned,
      filterFunc: ({pickup, terminalOffset}) => isNotAssignedPickup({pickup, terminalOffset}),
    },
  ];
};

const getDefaultSelectedFilters = () => {
  return {
    [PICKUP_OPTIMYZER_FILTER_NAME.isAssigned]: false,
    [PICKUP_OPTIMYZER_FILTER_NAME.isUnassigned]: true,
  };
};

const getSelectedPickupsByPickupId = (
  pickupListForOptimization,
  selectedPickupIdList
) => {
  return pickupListForOptimization.filter((pickup) =>
    selectedPickupIdList.includes(pickup.pickupId.toLowerCase())
  );
};

const isUnassignedPickupSelected = (
  pickupListForOptimization,
  selectedPickupIdList
) => {
  return pickupListForOptimization.some(
    (pickup) =>
      selectedPickupIdList.includes(pickup.pickupId.toLowerCase()) &&
      !pickup.routeId
  );
};

const isPendingAssignedPickupSelected = (
  pickupListForOptimization,
  selectedPickupIdList
) => {
  return pickupListForOptimization.some(
    (pickup) =>
      selectedPickupIdList.includes(pickup.pickupId.toLowerCase()) &&
      pickup.routeId
  );
};

const getAllUnassignedPickups = (pickupsList) => {
  return pickupsList.filter((pickup) => !pickup.routeId);
};

const getAllAssignedPickups = (pickupsList) => {
  return pickupsList.filter((pickup) => pickup.routeId);
};

const getShowPickupsListWhenPickupsSelectedInGrid = (
  pickupListForOptimization,
  selectedPickupIdList
) => {
  const assignedPickupSelected = isPendingAssignedPickupSelected(
    pickupListForOptimization,
    selectedPickupIdList
  );
  if (
    assignedPickupSelected &&
    isUnassignedPickupSelected(pickupListForOptimization, selectedPickupIdList)
  ) {
    return pickupListForOptimization;
  }
  if (assignedPickupSelected) {
    return getAllAssignedPickups(pickupListForOptimization);
  }
  return getAllUnassignedPickups(pickupListForOptimization);
};

const getPickupFormattedData = ({ pickupList, routeStop, routesList }) => {
  const previousDriver = routeStop.previousDriverId
    ? findDriverRoute(routesList, routeStop.previousDriverId)
    : null;
  const pickupData = pickupList.find(
    (pickup) =>
      pickup.pickupId.toLowerCase() === routeStop.pickupId.toLowerCase()
  ); //format virtual stop's key name based on map popup requirement
  return pickupData
    ? {
        ...routeStop,
        ...pickupData,
        latitude: pickupData.shipperLatitude,
        longitude: pickupData.shipperLongitude,
        stopType: appConstants.STOP_TYPE.Pickup,
        name: pickupData.shipperName,
        address: pickupData.shipperAddress,
        readyTime: pickupData.pickupReadyTime,
        closeTime: pickupData.pickupCloseTime,
        arrivalTime: routeStop.estimatedTimeOfArrival,
        departureTime: routeStop.estimatedTimeOfDeparture,
        previousDriver: previousDriver,
        address2: pickupData.address2,
        city: pickupData.shipperCity,
        state: pickupData.shipperState,
        zip: pickupData.shipperZip,
      }
    : {};
};

const findDriverRoute = (routesList, driverId) => {
  return routesList.find(
    (route) => route.driverId.toLowerCase() === driverId?.toLowerCase()
  );
};

const getRouteInfo = (recommendedDriver) => {
  return {
    driverId: recommendedDriver?.driverId,
    driverName: recommendedDriver?.driverName,
    driverNumber: recommendedDriver?.driverNumber,
    color: recommendedDriver?.color,
  };
};

const getStopInfo = ({
  routeStops,
  pickupList,
  routesList,
  recommendedDriver,
}) => {
  return routeStops.map((routeStop) => {
    if (routeStop.isVirtualStop) {
      //if stop is virtual stop, find data based on pickupId and return
      const stopData = getPickupFormattedData({
        pickupList,
        routeStop,
        routesList,
      });
      return {
        ...stopData,
        ...getRouteInfo(recommendedDriver),
      };
    } else {
      //if stop is not virtual stop return stop data from existing route stop
      const recommendedDriverStop = recommendedDriver.stops.find(
        (stop) => stop.stopId.toLowerCase() === routeStop.stopId.toLowerCase()
      );
      return recommendedDriverStop
        ? {
            ...recommendedDriver.stops.find(
              (stop) =>
                stop.stopId.toLowerCase() === routeStop.stopId.toLowerCase()
            ),
            ...routeStop,
            ...getRouteInfo(recommendedDriver),
          }
        : {};
    }
  });
};

const getUnassignPickupInfo = ({
  driverAssignment,
  pickupList,
  routesList,
}) => {
  return driverAssignment.pickupIds
    .map((pickupId) => {
      return pickupList.find(
        (pickup) => pickup.pickupId.toLowerCase() === pickupId.toLowerCase()
      );
    })
    .map((pickup) => {
      const previousDriver = pickup.driverId
        ? findDriverRoute(routesList, pickup.driverId)
        : null; // for already assign pickup search assigned driver and show it as a previous driver
      return {
        ...pickup,
        driverId: null,
        previousDriver,
      };
    });
};

const getDriverAssignmentsList = (
  pickupList,
  routesList,
  driverAssignments
) => {
  return driverAssignments.map((driverAssignment) => {
    if (driverAssignment.driverId) {
      const recommendedDriver = findDriverRoute(
        routesList,
        driverAssignment.driverId
      );
      const stops = getStopInfo({
        routeStops: driverAssignment.routeStops,
        pickupList,
        routesList,
        recommendedDriver,
      });
      return {
        ...recommendedDriver, // new Driver
        ...driverAssignment,
        stops, // new stopList including virtual stops
      };
    } else {
      //when pickup is unassign we will show pickup data from store
      const unassignPickupData = getUnassignPickupInfo({
        driverAssignment,
        pickupList,
        routesList,
      });
      return {
        isUnassignedPickup: true,
        ...driverAssignment,
        ...unassignPickupData[0],
      };
    }
  });
};

const countAssignedPickups = (acc, pickup) =>
  pickup.routeId && pickup.driverId ? acc + 1 : acc;

const countUnassignedPickups = (acc, pickup) =>
  !pickup.routeId && !pickup.driverId ? acc + 1 : acc;

const getTotalAssignedPickups = (pickupsList) => {
  return pickupsList.reduce(countAssignedPickups, 0);
};

const getTotalUnassignedPickups = (pickupsList) => {
  return pickupsList.reduce(countUnassignedPickups, 0);
};

const getTotalAssignedPickupsSelected = (selectedPickupsList) => {
  return selectedPickupsList.reduce(countAssignedPickups, 0);
};

const getTotalUnassignedPickupsSelected = (selectedPickupsList) => {
  return selectedPickupsList.reduce(countUnassignedPickups, 0);
};

const isPartialAssignedPickupsSelected = (selectedPickupsList, pickupsList) => {
  const totalAssignedPickupsSelected =
    getTotalAssignedPickupsSelected(selectedPickupsList);
  return (
    totalAssignedPickupsSelected > 0 &&
    totalAssignedPickupsSelected < getTotalAssignedPickups(pickupsList)
  );
};

const isPartialUnassignedPickupsSelected = (
  selectedPickupsList,
  pickupsList
) => {
  const totalUnassignedPickupsSelected =
    getTotalUnassignedPickupsSelected(selectedPickupsList);
  return (
    totalUnassignedPickupsSelected > 0 &&
    totalUnassignedPickupsSelected < getTotalUnassignedPickups(pickupsList)
  );
};

const isPickupSelectedInGrid = (selectedPickupIdList) => {
  return selectedPickupIdList.length;
};

const getReassignPickupDetail = (selectedPickupsToOptimize) => {
  return selectedPickupsToOptimize.reduce((acc, pickup) => {
    return [
      ...acc,
      ...(pickup.isUnassignedPickup
        ? [
            {
              pickupId: pickup.pickupId,
              driverIdToUnassign: pickup.previousDriver?.driverId ?? null,
              driverIdToAssign: pickup.driverId,
            },
          ]
        : pickup.stops.map((stop) => ({
            pickupId: stop.pickupId,
            driverIdToUnassign: stop.previousDriver?.driverId ?? null,
            driverIdToAssign: stop.driverId,
          }))),
    ];
  }, []);
};

const getSelectedPickupsToOptimizationCount = (selectedPickupsToOptimize) => {
  return selectedPickupsToOptimize.reduce((acc, selectedPickup) => {
    if (selectedPickup.isUnassignedPickup) {
      return acc + 1;
    }
    return acc + selectedPickup.stops.length;
  }, 0);
};

const getAvailablePickupForOptimization = (driverAssignmentsList) => {
  return driverAssignmentsList.reduce((acc, driverAssignment) => {
    if (driverAssignment.driverId && driverAssignment) {
      return (
        acc + driverAssignment.stops.filter((stop) => stop.isVirtualStop).length
      );
    }
    return acc + 1;
  }, 0);
};

const getDriverPickupIds = (driverAssignment) => {
  return driverAssignment.map((route) => route.pickupIds).flat();
};

const getSelectedDriverRouteBasedOnPickup = (
  driverAssignmentsList,
  selectedPickupId
) => {
  return driverAssignmentsList.filter(
    (driverAssignment) =>
      driverAssignment.driverId &&
      driverAssignment.pickupIds.some((v) => selectedPickupId.includes(v))
  );
};

const getOptimizeSolutionWithDriver = (driverAssignmentsList) => {
  return driverAssignmentsList.filter(
    (driverAssignment) => driverAssignment.driverId
  );
};

const selectedDriverAssignments = (
  driverAssignmentList,
  selectedDriverAssignmentId
) => {
  return driverAssignmentList.filter((driverAssignment) =>
    selectedDriverAssignmentId.includes(driverAssignment.driverId)
  );
};

const getSelectedStopsList = ({
  selectedDriverAssignment,
  selectedPickupId,
  routesList,
}) => {
  return selectedDriverAssignment.map((route) => ({
    ...route,
    stops: route.stops.filter((stop) =>
      selectedPickupId.includes(stop.pickupId?.toLowerCase())
    ),
    previousDriver: route.previousDriverId
      ? findDriverRoute(routesList, routeStop.previousDriverId)
      : null,
  }));
};

const removePickupIdFromList = (pickupIdList, pickupId) => {
  return pickupIdList.filter((pickup) => pickup.pickupId !== pickupId);
};

const getKPIDetails = (reassignPickupDetail, startTime) => {
  return {
    timeRequired: convertMillisecondIntoMin(new Date() - startTime),
    pickupsAssigned: reassignPickupDetail.length,
    driversInvolved: getDriversInvolvedCount(reassignPickupDetail),
  };
};

const getDriversInvolvedCount = (reassignPickupDetail) => {
  return uniq(
    reassignPickupDetail.reduce((driverList, pickup) => {
      return pickup.driverIdToUnassign
        ? [pickup.driverIdToAssign, pickup.driverIdToUnassign, ...driverList]
        : [pickup.driverIdToAssign, ...driverList];
    }, [])
  ).length;
};

const convertMillisecondIntoMin = (milliseconds) => {
  const date = new Date(Date.UTC(0, 0, 0, 0, 0, 0, milliseconds));
  const parts = [];
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();
  hours && parts.push(`${hours} hour `);
  minutes && parts.push(`${minutes} min `);
  seconds && parts.push(`${seconds} sec `);
  return parts.join(" ");
};

export {
  getDefaultFilters,
  getDefaultSelectedFilters,
  getSelectedPickupsByPickupId,
  isUnassignedPickupSelected,
  getShowPickupsListWhenPickupsSelectedInGrid,
  getAllUnassignedPickups,
  getDriverAssignmentsList,
  isPendingAssignedPickupSelected,
  isPartialUnassignedPickupsSelected,
  isPartialAssignedPickupsSelected,
  getAllAssignedPickups,
  isPickupSelectedInGrid,
  getReassignPickupDetail,
  getDriverPickupIds,
  getSelectedDriverRouteBasedOnPickup,
  getOptimizeSolutionWithDriver,
  selectedDriverAssignments,
  getSelectedStopsList,
  findDriverRoute,
  removePickupIdFromList,
  getSelectedPickupsToOptimizationCount,
  getAvailablePickupForOptimization,
  getKPIDetails,
};
